import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { usePosts } from '@/hooks/usePosts';
import { useCategories } from '@/hooks/useCategories';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Pagination from '@/components/base/Pagination';
import CatLoader from '@/components/base/CatLoader';
import AdSenseAd from '@/components/ads/AdSenseAd';
import Seo from '@/components/base/Seo';

const POSTS_PER_PAGE = 10;

export default function CategoryPage() {
  const { cat } = useParams<{ cat: string }>();
  const { getPostsByCategory, getPublishedPosts, loading: postsLoading } = usePosts();
  const { getCategoryById, loading: catsLoading } = useCategories();
  const { settings: siteSettings } = useSiteSettings();

  const catInfo = cat ? getCategoryById(cat) : undefined;
  const posts = cat ? getPostsByCategory(cat) : [];
  const allPosts = getPublishedPosts();

  const isLoading = postsLoading || catsLoading;

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });

  // Proposital delay when category changes
  const [categoryDelay, setCategoryDelay] = useState(true);

  const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));
  const categoryDelayMs = siteSettings?.categoryDelayMs ?? 5000;

  // reset to page 1 when category changes + dynamic delay
  useEffect(() => {
    setCategoryDelay(true);
    setCurrentPage(1);
    setSearchParams({});
    const timer = setTimeout(() => {
      setCategoryDelay(false);
    }, categoryDelayMs);
    return () => clearTimeout(timer);
  }, [cat]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep page in bounds
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const pagePosts = posts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  if (isLoading || categoryDelay) {
    return (
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <CatLoader fullPage message="Buscando os artigos da categoria..." />
        <Footer popularPosts={[]} />
      </div>
    );
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams(page === 1 ? {} : { page: String(page) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Seo
        title={`${catInfo?.label ?? cat} para gatos`}
        description={`Artigos, informações e dicas sobre ${catInfo?.label ?? cat} para ajudar você a cuidar melhor do seu gato.`}
        path={`/categoria/${cat}`}
      />
      <Navbar />

      <div>
        <div className="bg-background-100 py-8 sm:py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Link
                to="/"
                className="text-foreground-400 text-xs sm:text-sm hover:text-primary-500 cursor-pointer transition-colors"
              >
                Início
              </Link>
              <span className="text-foreground-300">/</span>
              <Link
                to="/blog"
                className="text-foreground-400 text-xs sm:text-sm hover:text-primary-500 cursor-pointer transition-colors"
              >
                Blog
              </Link>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-600 text-xs sm:text-sm">{catInfo?.label ?? cat}</span>
            </div>
            <span className="text-primary-500 text-xs font-bold tracking-widest uppercase block mb-2">
              Categoria
            </span>
            <h1 className="font-heading font-bold text-foreground-950 text-2xl sm:text-3xl md:text-4xl flex items-center gap-3">
              {catInfo && <i className={`${catInfo.icon} text-primary-500`}></i>}
              {catInfo?.label ?? cat}
            </h1>
            <p className="text-foreground-500 text-sm mt-2">
              {posts.length} artigo{posts.length !== 1 ? 's' : ''} encontrado{posts.length !== 1 ? 's' : ''}
              {totalPages > 1 && (
                <span className="ml-2 text-foreground-400">
                  — página {currentPage} de {totalPages}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <AdSenseAd placement="listing" className="mb-8 md:mb-10" />
          {posts.length === 0 ? (
            <div className="text-center py-16 md:py-20 text-foreground-500">
              <i className="ri-file-list-3-line text-4xl md:text-5xl mb-3 md:mb-4 block"></i>
              <p>Nenhum artigo nesta categoria ainda.</p>
              <Link
                to="/blog"
                className="text-primary-500 font-bold mt-3 md:mt-4 inline-block cursor-pointer hover:text-primary-700 transition-colors"
              >
                Ver todos os artigos
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {pagePosts.map((post, i) => (
                  <div
                    key={post.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${i * 50}ms`, animationFillMode: 'both' }}
                  >
                    <ArticleCard post={post} />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />

              {/* Post range info */}
              {totalPages > 1 && (
                <p className="text-center text-foreground-400 text-xs mt-4">
                  Exibindo {(currentPage - 1) * POSTS_PER_PAGE + 1}–{Math.min(currentPage * POSTS_PER_PAGE, posts.length)} de {posts.length} artigos
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <Footer popularPosts={allPosts} />
    </div>
  );
}
